import { Dispatch, FunctionComponent, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { ProductType } from '@/types';
import { saveSharingDraft } from '@/utils/http/saveSharingDraft';
import { t } from '@/utils/i18n';
import { getDraftImageUrl } from '@/utils/imageUrls/getDraftImageUrl';
import { getProductImageUrl } from '@/utils/imageUrls/getProductImageUrl';
import { isCYOMode, isEmbroideryMode } from '@/utils/params';
import { SharingLinks, generateShareUrl } from '@/utils/sharing';
import { useAsyncEffect } from '@/hooks/useAsyncEffect';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getDraftForSaving } from '@/model/draft/getDraftForSaving';
import { getProductType } from '@/model/draft/getProductType';
import { getViewForPreview } from '@/model/draft/getViewForPreview';
import { saveDraftAction } from '@/redux/product/actions/saveDraftAction';
import { saveProductAction } from '@/redux/product/actions/saveProductAction';
import { StoreDispatch, StoreState } from '@/redux/types';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import { useFocus } from '@/components/common/focus';
import { Panel, PanelContent, PanelHeader } from '@/components/common/panel/panel';
import { SaveDialog, SaveDialogState, SaveDialogStateAction, useSaveDialog } from '../SaveDialog';
import { SharingLink } from './SharingLink';
import { SharingPreview } from './SharingPreview';
import { SharingSocial } from './SharingSocial';
import './Sharing.scss';

export const SHARE_ROUTE = '/share';
export const SHARE_MEMENTO_ROUTE = `${SHARE_ROUTE}/memento`;

interface Props {
  saveState: SaveDialogState;
  dispatch: Dispatch<SaveDialogStateAction>;
}

const SharingContent: FunctionComponent<Props> = ({ saveState, dispatch }) => {
  const store = useStore<StoreState>();
  const storeDispatch = useDispatch<StoreDispatch>();

  const isShareMemento = Boolean(useMatch(SHARE_MEMENTO_ROUTE));

  const [sharedProductType, setSharedProductType] = useState<ProductType>();
  const [sharedUrls, setSharedUrls] = useState<SharingLinks>();
  const [sharedId, setSharedId] = useState<string>();
  const [sharedPreview, setSharedPreview] = useState<string>();

  useAsyncEffect(async () => {
    // changes to the product state should not trigger another share
    // opening the panel shares the state at this point in time
    const draft = await getDraftForSaving(store, isShareMemento);

    dispatch({ type: 'save-start', payload: getConfigurations(draft) });
    setSharedProductType(getProductType(draft));
    const onUpdate = () => dispatch({ type: 'save-update' });
    const onSuccess = () => dispatch({ type: 'save-success' });
    const onError = (error: unknown) => dispatch({ type: 'save-error', payload: error });

    if (isCYOMode()) {
      try {
        const sharingDraft = await storeDispatch(
          saveDraftAction(draft, saveSharingDraft, onUpdate)
        );
        const urls = await generateShareUrl(
          {
            draftId: sharingDraft.id,
            viewId: sharingDraft.viewId,
            productTypeId: sharingDraft.productTypeId,
            embroidery: isEmbroideryMode(),
          },
          true
        );
        setSharedId(sharingDraft.id);
        setSharedUrls(urls);
        setSharedPreview(getDraftImageUrl(sharingDraft, 300));
        onSuccess();
      } catch (error) {
        onError(error);
      }
    } else {
      try {
        const productReference = await storeDispatch(saveProductAction(onUpdate));
        const productId = productReference.lookupId || productReference.id;
        const { id: viewId } = getViewForPreview(draft);
        const urls = await generateShareUrl({ productId, viewId });
        setSharedId(productId);
        setSharedUrls(urls);
        setSharedPreview(getProductImageUrl(productId, viewId));
        onSuccess();
      } catch (error) {
        onError(error);
      }
    }
  }, []);

  return (
    <PanelContent>
      <div className="sharing">
        {sharedProductType && (
          <SharingPreview
            productType={sharedProductType}
            id={sharedId}
            previewImageUrl={sharedPreview}
          />
        )}
        {sharedUrls && <SharingLink url={sharedUrls.shortUrl} />}
        {sharedUrls && <SharingSocial url={sharedUrls.url} imageUrl={sharedPreview} />}
      </div>
      {saveState.loading && <LoadingOverlay />}
      <SaveDialog saveState={saveState} dispatch={dispatch} />
    </PanelContent>
  );
};

interface SharingProps {
  active: boolean;
  onClose: () => void;
}

export const Sharing: FunctionComponent<SharingProps> = ({ active, onClose }) => {
  const [panel, focusPanel] = useFocus(active, onClose);
  const [saveState, dispatch] = useSaveDialog();

  const headline = t('share.title');

  return (
    <Panel ref={panel} active={active} onEntered={focusPanel}>
      <PanelHeader label={t('modal.close', headline)} onClose={onClose}>
        {headline}
      </PanelHeader>
      <SharingContent saveState={saveState} dispatch={dispatch} />
    </Panel>
  );
};
